import { useQuery } from '@tanstack/react-query';
import { formatDateAndTime } from '@/utils/formatDate';

import { useEffect, useState, useMemo } from 'react';
import { RoRoPageData } from '@/types/dashboard/RoRoPage';
import { getArmadoresList } from '@/services/getListArmadores';
import {
  getDashboardRoRoPageData,
  getDashboardRoRoPageViajesData,
} from '@/services/dashboardRoRoPage';
import useAuthContext from '../useAuthContext';

export const useRoRoPage = () => {
  const { userDataDecoded } = useAuthContext();
  const [roRoPageData, setRoRoPageData] = useState<RoRoPageData | null>(null);
  const [loading, setLoading] = useState(true);

  // Obtener la lista de armadores
  const { data: armadoresList, isLoading: loadingArmadores } = useQuery({
    queryKey: ['armadoresListDropdownRoRo'],
    queryFn: getArmadoresList,
    refetchOnWindowFocus: false,
  });

  const armadoresListForDropdown = useMemo(() => {
    const options = userDataDecoded?.armadores?.split(';').map((val) => {
      return { value: val, name: val };
    });
    const shipownerOptions = armadoresList?.map(
      (val: { codigo: string; nombre: string }) => {
        return { value: val.codigo, name: `${val.nombre}` };
      }
    );
    return userDataDecoded?.armadores === '*' ? shipownerOptions : options;
  }, [userDataDecoded, armadoresList]);

  const [currentArmadorRoRo, setCurrentArmadorRoRo] = useState(() => ({
    value: '',
    name: '',
  }));

  // Obtener la lista de viajes
  const { data: viajesList, isLoading: loadingViajes } = useQuery({
    queryKey: ['viajesListDropdownRoRo', currentArmadorRoRo.value],
    queryFn: () =>
      getDashboardRoRoPageViajesData({ armador: currentArmadorRoRo.value }),
    enabled: currentArmadorRoRo.value !== '', // Habilitar solo si currentArmador.value no está vacío
    refetchOnWindowFocus: false,
  });

  const viajesListForDropdownRoRo = useMemo(() => {
    return viajesList
      ?.sort(
        (a, b) => new Date(b.llegada).getTime() - new Date(a.llegada).getTime()
      ) // Sorting by salida (newest first)
      .map(
        (val: {
          codigo: string;
          buque: string;
          salida: string;
          llegada: string;
        }) => {
          return {
            value: val.codigo,
            name: `${val.buque}  - ${formatDateAndTime(val.llegada)}`,
            llegada: val.llegada,
            salida: val.salida,
          };
        }
      );
  }, [viajesList]);

  const [currentViajeRoRo, setCurrentViajeRoRo] = useState(
    () =>
      viajesListForDropdownRoRo?.[0] || {
        value: '',
        name: '',
        llegada: '',
        salida: '',
      }
  );

  // Obtener los datos de la página basado en el viaje actual
  const { data: pageData, isLoading: loadingPageData } = useQuery({
    queryKey: [
      'roRoPageData',
      currentViajeRoRo.value,
      currentArmadorRoRo.value,
    ], // Asegurar que currentViaje.value es parte de la queryKey
    queryFn: () =>
      getDashboardRoRoPageData({
        viaje: Number(currentViajeRoRo.value),
        armador: currentArmadorRoRo.value,
      }),
    enabled: currentViajeRoRo.value !== '' && currentArmadorRoRo.value !== '', // Habilitar solo si currentViaje.value no está vacío
  });

  useEffect(() => {
    if (pageData) {
      setRoRoPageData(pageData);
    }
  }, [pageData, currentArmadorRoRo]);

  useEffect(() => {
    if (!loadingArmadores && !loadingViajes && !loadingPageData && pageData) {
      setLoading(false);
    }
  }, [loadingArmadores, loadingViajes, loadingPageData, pageData]);

  useEffect(() => {
    if (viajesListForDropdownRoRo && viajesListForDropdownRoRo.length > 0) {
      setCurrentViajeRoRo(viajesListForDropdownRoRo[0]);
    } else {
      setCurrentViajeRoRo({ value: '', name: '', llegada: '', salida: '' });
    }
  }, [viajesListForDropdownRoRo, currentArmadorRoRo]);

  return {
    armadoresListForDropdown,
    currentArmadorRoRo,
    setCurrentArmadorRoRo,
    currentViajeRoRo,
    setCurrentViajeRoRo,
    viajesListForDropdownRoRo,
    roRoPageData,
    setRoRoPageData,
    viajesList,
    pageData,
    loadingArmadores,
    loadingViajes,
    loadingPageData,
    loading,
  };
};
